/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import Loader from "../../components/Common/Loader";
import RecipeDetailsModal from "./RecipeDetailsModal";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { createMealPlanThunk } from "../../redux/Slices/clientMealsTabsSlice";
import appInsights from "../../AppInsights";

const MealPlanModal = ({
  showModal,
  setShowModal,
  mealPlanDetails,
  modalLoading,
  mealModalType,
  onMealPlanUpdated,
}) => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  const { profileData } = useSelector((state) => state.profile);

  const { createMealPlanSuccessMessage, createMealPlanLoading } = useSelector(
    (state) => state.mealTags
  );
  const [selectedRecipe, setSelectedRecipe] = useState({});
  const [showInfoModal, setShowInfoModal] = useState(false);

  if (!showModal) return null;

  const handleInfoClick = (e, recipe) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedRecipe(recipe);
    setShowInfoModal(true);
  };

  const handleUpsertMealPlan = () => {
    const cleaned = Object.fromEntries(
      Object.entries(mealPlanDetails?.mealsPerDayPercentage || {})
        .filter(([_, v]) => v !== null) // remove null values
        .map(([k, v]) => [k.toUpperCase(), v]) // convert keys to caps
    );
    const coachId = profileData?.coachId;

    const requestPayload = {
      dailyIntake: mealPlanDetails?.dailyIntake,
      bmrTdee:
        mealPlanDetails?.bmrTdee === "N/A"
          ? "0"
          : mealPlanDetails?.bmrTdee || "0",
      pal: mealPlanDetails?.pal === "N/A" ? 0 : mealPlanDetails?.pal || 0,
      preferences: mealPlanDetails?.preferences?.join(",") || "",
      allergies: mealPlanDetails?.allergies?.join(",") || "",
      mealsPerDay: mealPlanDetails?.mealsPerDay?.join(",") || "",
      proteinPercentage: mealPlanDetails?.proteinPercentage,
      carbsPercentage: mealPlanDetails?.carbsPercentage,
      fatPercentage: mealPlanDetails?.fatPercentage,
      proteinGrams: mealPlanDetails?.proteinGrams,
      carbsGrams: mealPlanDetails?.carbsGrams,
      fatGrams: mealPlanDetails?.fatGrams,
      mealPlanName: mealPlanDetails?.mealPlanName,
      clientId: mealPlanDetails?.clientId,
      coachId: coachId,
      recipesDetailMeal: mealPlanDetails?.recipesDetailMeal,
      mealsPerDayPercentage: cleaned,
      macroSplitTemplateId: mealPlanDetails?.macroSplitTemplateId,
      numberOfRecipesPerMeal: mealPlanDetails?.numberOfRecipesPerMeal,
    };

    dispatch(createMealPlanThunk(requestPayload)).then(()=>{
      // Track success
      appInsights.trackEvent({
        name: "AI_MealPlan_Update_Success",
        properties: {
          clientId: mealPlanDetails?.clientId,
          mealPlanName: mealPlanDetails?.mealPlanName,
        },
      });
      onMealPlanUpdated?.();
      
      setShowModal(false);
    }).catch((error)=>{
      // Track failure
      setShowModal(false);
      appInsights.trackException({
        exception: new Error(error),
        properties: {
          action: "AI_MealPlan_Update_Failed",
          clientId: mealPlanDetails?.clientId,
          mealPlanName: mealPlanDetails?.mealPlanName,
        },
      });
    });
  };
  const handleCloseDrawer = () => {
    setShowInfoModal(false);
    setSelectedRecipe({});
  };

  return (
    <>
      <div
        className="modal-overlay"
        onClick={(e) => {
          if (e.target.className === "modal-overlay") setShowModal(false);
        }}
        style={{ zIndex: 1040 }} // Ensure modal is below drawer
      >
        <div
          className={`modal-content ${
            modalLoading ? "modal-content-loading" : ""
          }`}
        >
          <div className="modal-header">
            <h4>Meal Plan Details</h4>
            <button
              className="modal-close"
              onClick={() => setShowModal(false)}
              disabled={modalLoading}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="ai-modal-body">
            {modalLoading ? (
              <Loader />
            ) : (
              <>
                {mealPlanDetails?.mealPlanName ? (
                  <div className="view-section">
                    <h5 className="view-section-title">
                      {mealPlanDetails?.mealPlanName || "Current Meal Plan"}
                    </h5>

                    <div className="view-item">
                      <span className="view-label">Daily Intake:</span>
                      <span className="view-value">
                        {mealPlanDetails?.dailyIntake} calories
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">Meals per Day:</span>
                      <span className="view-value">
                        {mealPlanDetails?.mealsPerDay?.[0]}
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">BMR / TDEE:</span>
                      <span className="view-value">
                        {mealPlanDetails?.bmrTdee}
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">PAL:</span>
                      <span className="view-value">{mealPlanDetails?.pal}</span>
                    </div>

                    <div className="view-section-divider"></div>

                    <div className="view-item">
                      <span className="view-label">Protein:</span>
                      <span className="view-value">
                        {mealPlanDetails?.proteinGrams}g (
                        {mealPlanDetails?.proteinPercentage}%)
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">Carbs:</span>
                      <span className="view-value">
                        {mealPlanDetails?.carbsGrams}g (
                        {mealPlanDetails?.carbsPercentage}%)
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">Fat:</span>
                      <span className="view-value">
                        {mealPlanDetails?.fatGrams}g (
                        {mealPlanDetails?.fatPercentage}%)
                      </span>
                    </div>

                    <div className="view-section-divider"></div>

                    <h6 className="view-subsection-title">Meal Distribution</h6>
                    {mealPlanDetails?.mealsPerDayPercentage?.BREAKFAST && (
                      <div className="view-item">
                        <span className="view-label">Breakfast:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.BREAKFAST}%
                        </span>
                      </div>
                    )}
                    {mealPlanDetails?.mealsPerDayPercentage?.LUNCH && (
                      <div className="view-item">
                        <span className="view-label">Lunch:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.LUNCH}%
                        </span>
                      </div>
                    )}
                    {mealPlanDetails?.mealsPerDayPercentage?.LUNCH1 && (
                      <div className="view-item">
                        <span className="view-label">Lunch1:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.LUNCH1}%
                        </span>
                      </div>
                    )}
                    {mealPlanDetails?.mealsPerDayPercentage?.DINNER && (
                      <div className="view-item">
                        <span className="view-label">Dinner:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.DINNER}%
                        </span>
                      </div>
                    )}
                    {mealPlanDetails?.mealsPerDayPercentage?.SNACK && (
                      <div className="view-item">
                        <span className="view-label">Snack:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.SNACK}%
                        </span>
                      </div>
                    )}
                    {mealPlanDetails?.mealsPerDayPercentage?.SNACK1 && (
                      <div className="view-item">
                        <span className="view-label">Snack1:</span>
                        <span className="view-value">
                          {mealPlanDetails?.mealsPerDayPercentage?.SNACK1}%
                        </span>
                      </div>
                    )}

                    <div className="view-section-divider"></div>

                    <h6 className="view-subsection-title">Today's Meals</h6>
                    <div className="meal-list">
                      {(() => {
                        console.log("test_mealPlanDetails", mealPlanDetails);
                        const groupedMeals =
                          mealPlanDetails?.recipesDetailMeal?.reduce(
                            (acc, mealDetails) => {
                              const mealName = mealDetails.mealName;
                              const recipe = mealDetails.recipe || {};

                              if (!acc[mealName]) {
                                acc[mealName] = [];
                              }
                              acc[mealName].push(recipe);
                              return acc;
                            },
                            {}
                          );

                        return Object.entries(groupedMeals || {}).map(
                          ([mealName, recipes]) => (
                            <div key={mealName} className="meal-category mb-4">
                              <h6 className="meal-category-title text-primary mb-3">
                                {mealName} ({recipes.length}{" "}
                                {recipes.length === 1 ? "Recipe" : "Recipes"})
                              </h6>
                              <div className="recipes-scroll-container">
                                <div className="recipes-scroll-wrapper">
                                  <div className="recipes-grid d-flex flex-wrap gap-3">
                                    {recipes.map((recipe, index) => (
                                      <div
                                        className="card recipe-card position-relative p-0 w-200 cursor-pointer"
                                        key={`${mealName}-${
                                          recipe.recipeId || index
                                        }`}
                                      >
                                        <div className="info-icon position-absolute top-0 end-0 p-2">
                                          <div
                                            className="d-flex justify-content-end position-absolute top-0 end-5 p-2 gap-2"
                                            style={{ right: "-5px" }}
                                          >
                                            <div
                                              onClick={(e) =>
                                                handleInfoClick(e, recipe)
                                              }
                                              className="icon-circle"
                                              style={{
                                                cursor: "pointer",
                                                zIndex: 10,
                                              }}
                                            >
                                              <i className="bi bi-info-circle-fill text-white fs-7"></i>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="recipe-overlay">
                                          <h6 className="card-title">
                                            {recipe?.title || recipe?.name}
                                          </h6>
                                          <div className="full-title">
                                            {recipe?.title || recipe?.name}
                                          </div>
                                        </div>

                                        <img
                                          src={
                                            recipe?.mainImage ||
                                            "/placeholder-image.jpg"
                                          }
                                          alt={recipe?.title || recipe?.name}
                                          className="card-img-top recipe-image visible"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        );
                      })()}
                    </div>

                    <div className="view-section-divider"></div>

                    <h6 className="view-subsection-title">
                      Dietary Information
                    </h6>
                    <div className="view-item">
                      <span className="view-label">Allergies:</span>
                      <span className="view-value">
                        {mealPlanDetails?.allergies?.length > 0
                          ? mealPlanDetails.allergies.join(", ")
                          : "None"}
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">Preferences:</span>
                      <span className="view-value">
                        {mealPlanDetails?.preferences?.length > 0
                          ? mealPlanDetails.preferences.join(", ")
                          : "None"}
                      </span>
                    </div>
                    <div className="view-item">
                      <span className="view-label">Last Updated:</span>
                      <span className="view-value">
                        {mealPlanDetails?.lastUpdated || "15 hours ago"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="middle-text-noplan">No meal plan</div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            {/* <button
              className="btn btn-secondary"
              onClick={() => setShowModal(false)}
            >
              Close
            </button> */}
            <Button
              variant="outline-primary"
              onClick={() => setShowModal(false)}
              size="sm"
              className="cancel-button w-25 p-0"
              // disabled={isUpdating}
            >
               {mealModalType === "update" ? trans("Common.cancel") : "Close"}
            </Button>
            {mealModalType === "update" && (    <Button      className="saveButton w-25 ml-2 p-0"
                  onClick={handleUpsertMealPlan}      
                  disabled={createMealPlanLoading}>
                    {createMealPlanLoading? "Updating...": trans("Common.update")}</Button>)}
          </div>
        </div>
      </div>

      {/* Render RecipeDrawer outside modal to prevent z-index issues */}
      <RecipeDetailsModal
        recipeDetails={selectedRecipe}
        visible={showInfoModal}
        onClose={handleCloseDrawer}
      />
    </>
  );
};

export default MealPlanModal;
